import React from "react";
import { Box, Typography, IconButton, Button, alpha } from "@mui/material";
import { ShoppingCart, Info } from "@mui/icons-material";
import { formatCurrency } from "../../../utils/currencyUtils";

const OfferItem = ({ item, imageUrl, onAddToCart, onViewDetails }) => {
  const calculateDiscountedPrice = (item) => {
    const price = parseFloat(item.price || 0);
    const discount = parseFloat(item.discount || 0);
    return discount > 0 ? price - (price * discount) / 100 : price;
  };

  const discountedPrice = calculateDiscountedPrice(item);
  const originalPrice = parseFloat(item.price || 0);
  const discount = parseFloat(item.discount || 0);

  return (
    <Box
      sx={{
        minWidth: { xs: 160, sm: 180, md: 200 },
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        position: "relative",
        pt: 1,
        "&:hover .cart-icon": {
          opacity: 1,
          top: "25%",
        },
        "&:hover .info-icon": {
          opacity: 1,
          top: "75%",
        },
        "&:hover .circular-image": {
          transform: "scale(1.05)",
          boxShadow: `0 12px 32px ${alpha("#f59e0b", 0.3)}, 0 0 0 2px ${alpha("#f59e0b", 0.4)}`,
        },
      }}
    >
      {/* Circular Image Container */}
      <Box
        sx={{
          position: "relative",
          mb: 1.5,
        }}
      >
        {/* Discount Badge */}
        {discount > 0 && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              bgcolor: "#dc2626",
              color: "white",
              borderRadius: "16px",
              px: 1.25,
              py: 0.4,
              fontSize: "0.7rem",
              fontWeight: 700,
              boxShadow: `0 2px 8px ${alpha("#dc2626", 0.4)}`,
              zIndex: 2,
            }}
          >
            {discount}% OFF
          </Box>
        )}

        {/* Cart Icon on Hover - Top Right */}
        <IconButton
          className="cart-icon"
          onClick={() => onAddToCart(item)}
          sx={{
            position: "absolute",
            top: "50%",
            right: -15,
            bgcolor: "white",
            width: 44,
            height: 44,
            opacity: 0,
            transform: "translate(0, -50%)",
            transition: "all 0.3s ease",
            zIndex: 3,
            boxShadow: `0 4px 12px ${alpha("#000000", 0.15)}`,
            "&:hover": {
              bgcolor: "#f59e0b",
              color: "white",
              transform: "translate(0, -50%) scale(1.1)",
              boxShadow: `0 6px 16px ${alpha("#f59e0b", 0.4)}`,
            },
          }}
        >
          <ShoppingCart sx={{ fontSize: 22 }} />
        </IconButton>

        {/* Info Icon on Hover - Below Cart Icon */}
        <IconButton
          className="info-icon"
          onClick={(e) => {
            e.stopPropagation();
            onViewDetails(item);
          }}
          sx={{
            position: "absolute",
            top: "50%",
            right: -15,
            bgcolor: "white",
            width: 44,
            height: 44,
            opacity: 0,
            transform: "translate(0, -50%)",
            transition: "all 0.3s ease",
            zIndex: 3,
            boxShadow: `0 4px 12px ${alpha("#000000", 0.15)}`,
            "&:hover": {
              bgcolor: "#f59e0b",
              color: "white",
              transform: "translate(0, -50%) scale(1.1)",
              boxShadow: `0 6px 16px ${alpha("#f59e0b", 0.4)}`,
            },
          }}
        >
          <Info sx={{ fontSize: 22 }} />
        </IconButton>

        {/* Circular Image */}
        <Box
          className="circular-image"
          sx={{
            width: { xs: 140, sm: 160, md: 180 },
            height: { xs: 140, sm: 160, md: 180 },
            borderRadius: "50%",
            overflow: "hidden",
            border: `3px solid ${alpha("#f59e0b", 0.2)}`,
            boxShadow: `0 6px 20px ${alpha("#f59e0b", 0.2)}, 0 0 0 1px ${alpha("#ffffff", 0.8)}`,
            position: "relative",
            cursor: "pointer",
            transition: "all 0.3s ease",
          }}
          onClick={() => onViewDetails(item)}
        >
          <Box
            component="img"
            src={
              imageUrl ||
              "https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400&h=400&fit=crop"
            }
            alt={item.name}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        </Box>
      </Box>

      {/* Item Details */}
      <Box
        sx={{
          textAlign: "center",
          width: "100%",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: "#374151",
            fontSize: "0.9rem",
            mb: 0.5,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {item.name}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: "#6b7280",
            fontSize: "0.75rem",
            mb: 1.25,
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            minHeight: "2.25em",
          }}
        >
          {item.description || "Delicious offer"}
        </Typography>

        {/* Price */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            mb: 1.25,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: "#f59e0b",
              fontSize: "1.1rem",
            }}
          >
            {formatCurrency(discountedPrice, "Rs", { space: true })}
          </Typography>
          {discount > 0 && (
            <Typography
              variant="body2"
              sx={{
                color: "#9ca3af",
                textDecoration: "line-through",
                fontSize: "0.85rem",
              }}
            >
              {formatCurrency(originalPrice, "Rs", { space: true })}
            </Typography>
          )}
        </Box>

        {/* View Details Button */}
        {/* <Button
          variant="contained"
          size="small"
          startIcon={<Info sx={{ fontSize: 16 }} />}
          onClick={() => onViewDetails(item)}
          sx={{
            textTransform: "none",
            fontSize: "0.75rem",
            fontWeight: 700,
            background: `linear-gradient(135deg, #f59e0b 0%, #d97706 100%)`,
            color: "white",
            borderRadius: "12px",
            px: 2,
            py: 0.75,
            boxShadow: `0 2px 8px ${alpha("#f59e0b", 0.3)}`,
            "&:hover": {
              background: `linear-gradient(135deg, #d97706 0%, #b45309 100%)`,
              boxShadow: `0 4px 12px ${alpha("#f59e0b", 0.4)}`,
              transform: "translateY(-2px)",
            },
            transition: "all 0.2s ease",
          }}
        >
          View Details
        </Button> */}
      </Box>
    </Box>
  );
};

export default OfferItem;
